export const device_name = "Syrus4G LTE";
import {
  BleClient,
  RequestBleDeviceOptions,
  dataViewToText,
  ScanMode,
  BleDevice,
  textToDataView,
} from "@capacitor-community/bluetooth-le";
import { Plugins, Capacitor } from "@capacitor/core";
const { BluetoothSerial } = Plugins;
import { CRC } from "crc-full";
const S4_PREFIX_NAME = "Syrus 4";
const S4_SERVICE = "00000000-dc70-0080-dc70-a07ba85ee4d6";
const S4_MAIN_CONSOLE_CHARACTERISTIC = "00000000-dc74-0180-dc74-a07ba85ee4d6";
const S4_NOTIFY_CHARACTERISTIC = "00000000-dc74-0280-dc74-a07ba85ee4d6";
// const S4_USER_CONSOLE_CHARACTERISTIC = "00000000-dc74-0380-dc74-a07ba85ee4d6";
const KEEP_ALIVE_MESSAGE = "apx-bt ping";
const KEEP_ALIVE_TIME = 55 * 1000;
const MAX_TRIES = 3;
const TIMEOUT_SEND_AND_WAIT = 1000 * 10;
const CRCCalculator = new CRC(
  "CRC16_ARC",
  16,
  0x8005,
  0x0000,
  0x0000,
  true,
  true
);

function isValidJson(data: string) {
  try {
    JSON.parse(data);
    return true;
  } catch (error) {
    return false;
  }
}

export interface requestDeviceOptions {
  onDisconnect?: (id: string) => void;
}

export interface syrus4BluetoothResponse {
  len: string;
  id: number;
  res: unknown;
  crc: string;
}

export interface syrus4BluetoothMessage {
  cmd: unknown;
  id: number;
  crc: string;
  len: string;
}

interface callback {
  (messageData: syrus4BluetoothResponse): void;
}

interface EventCallback {
  (messageData: string): void;
}

function wait(timeout = 200): Promise<void> {
  return new Promise((res) => {
    setTimeout(() => {
      res();
    }, timeout);
  });
}

export class BluetoothService {
  device: BleDevice | null = null;
  buffer = "";
  bufferEvent = "";
  isSubscribed = false;
  isAuthtenticated = false;
  callbacks: callback[] = [];
  notificationsCallbacks: EventCallback[] = [];
  keepAliveInterval = 0;
  constructor() {
    //eslint-disable-next-line
    (window as any).$BleClient = BleClient;
  }

  async initialize(): Promise<void> {
    await BleClient.initialize();
  }

  isEnabled(): Promise<boolean> {
    return BleClient.getEnabled();
  }

  startEnabledNotifications(callback: (value: boolean) => void): void {
    BleClient.startEnabledNotifications(callback);
  }

  stopEnabledNotifications(): void {
    BleClient.stopEnabledNotifications();
  }

  async requestDevice(opts: requestDeviceOptions): Promise<void> {
    await this.initialize();
    if (Capacitor.getPlatform() == "android") {
      await BluetoothSerial.enable();
    }
    if (!(await this.isEnabled())) {
      throw new Error("Bluetooth is no enabled");
    }
    const options: RequestBleDeviceOptions = {
      optionalServices: [S4_SERVICE, S4_MAIN_CONSOLE_CHARACTERISTIC],
      namePrefix: S4_PREFIX_NAME,
      scanMode: ScanMode.SCAN_MODE_LOW_LATENCY,
    };
    const device = await BleClient.requestDevice(options);
    console.log("connecting to device", device);
    await BleClient.connect(device.deviceId, (id) => {
      this.device = null;
      this.isAuthtenticated = false;
      opts.onDisconnect && opts.onDisconnect(id);
      this.removeKeepAlive();
      this.removeAllEventListeners();
    });
    this.device = device;
    console.log("connected to device", device);
    this.subscribe();
  }

  async disconnect(): Promise<void> {
    if (!this.device) throw new Error("Device no connected");
    await BleClient.disconnect(this.device.deviceId);
  }

  async subscribe(): Promise<void> {
    if (!this.device) throw new Error("no device connected");
    console.log("starting notifications");
    await BleClient.startNotifications(
      this.device.deviceId,
      S4_SERVICE,
      S4_MAIN_CONSOLE_CHARACTERISTIC,
      (dataView) => {
        const data = dataViewToText(dataView);
        console.log("message received", data);
        this.concatenateToBuffer(data);
        if (isValidJson(this.buffer)) {
          for (const cb of this.callbacks) {
            cb(JSON.parse(this.buffer));
          }
          this.buffer = "";
        }
      }
    ).then(() => {
      console.log("listen for notifications");
      this.isSubscribed = true;
      return {
        unsubscribe: () => {
          if (!this.device) throw new Error("no device connected");
          BleClient.stopNotifications(
            this.device.deviceId,
            S4_SERVICE,
            S4_MAIN_CONSOLE_CHARACTERISTIC
          );
        },
      };
    });
  }

  async subscribeToNotifications(): Promise<void> {
    if (!this.device) throw new Error("no device connected");
    console.log("starting notifications for events");
    await BleClient.startNotifications(
      this.device.deviceId,
      S4_SERVICE,
      S4_NOTIFY_CHARACTERISTIC,
      (dataView) => {
        const data = dataViewToText(dataView);
        console.log("event received", data);
        this.concatenateToBufferEvent(data);
        if (isValidJson(this.bufferEvent)) {
          for (const cb of this.notificationsCallbacks) {
            cb(this.bufferEvent);
          }
          this.bufferEvent = "";
        }
      }
    ).then(() => {
      console.log("listen for notifications events");
      return {
        unsubscribe: () => {
          if (!this.device) throw new Error("no device connected");
          BleClient.stopNotifications(
            this.device.deviceId,
            S4_SERVICE,
            S4_NOTIFY_CHARACTERISTIC
          );
        },
      };
    });
  }

  addNotificationListener(cb: (str: string) => void): void {
    this.notificationsCallbacks.push(cb);
  }

  removeNotificationListener(cb: (str: string) => void): void {
    this.notificationsCallbacks.splice(
      this.notificationsCallbacks.findIndex((c) => c === cb),
      1
    );
  }

  removeAllNotificationListeners(): void {
    this.notificationsCallbacks = [];
  }

  toS4MEssage(_cmd: string): string {
    const cmd = `"${_cmd.replace(/"/gm, '\\"')}"`;
    const crc = CRCCalculator.compute(Buffer.from(cmd))
      .toString()
      .padStart(5, "0");
    const length = (52 + cmd.length).toString().padStart(5, "0");
    return `{"len":"${length}","id":${Math.floor(
      Date.now() / 1000
    ).toString()},"cmd":${cmd},"crc":"${crc}"}`;
  }

  addEventListener(cb: (str: syrus4BluetoothResponse) => void): void {
    this.callbacks.push(cb);
  }

  removeEventListener(cb: (str: syrus4BluetoothResponse) => void): void {
    this.callbacks.splice(
      this.callbacks.findIndex((c) => c === cb),
      1
    );
  }

  removeAllEventListeners(): void {
    this.callbacks = [];
  }

  async read(): Promise<string> {
    if (!this.device) throw new Error("Device no connected");
    const dataView = await BleClient.read(
      this.device.deviceId,
      S4_SERVICE,
      S4_MAIN_CONSOLE_CHARACTERISTIC
    );
    return dataViewToText(dataView);
  }

  async authenticate(pinCode: string): Promise<string> {
    if (!this.device) throw new Error("no device connected");
    console.log("starting authentication");
    const message = `apx-bt auth ${pinCode}`;
    const payload = this.toS4MEssage(message);
    try {
      const response = await this._sendAndWait(payload, 3);
      if (response.res === "authenticated") {
        this.isAuthtenticated = true;
        this.resetKeepAlive();
        return response.res;
      }
    } catch (error) {
      // Device no connected treated like Invalid Passphrase, pending message to Invalid Passphrase to change this to new Error("Device no response")
      throw new Error("Invalid Passphrase");
    }
    throw new Error("Invalid Passphrase");
  }

  async send(message: string): Promise<syrus4BluetoothResponse> {
    const payload = this.toS4MEssage(message);
    await this._send(payload);
    return JSON.parse(payload);
  }

  sendAndWait(message: string): Promise<syrus4BluetoothResponse> {
    const payload = this.toS4MEssage(message);
    return this._sendAndWait(payload);
  }

  private _sendAndWait(
    payload: string,
    tries = 1
  ): Promise<syrus4BluetoothResponse> {
    let timeout: number;
    return new Promise((resolve, reject) => {
      const waitResponse = (response: syrus4BluetoothResponse) => {
        if (response.id === JSON.parse(payload).id) {
          this.removeEventListener(waitResponse);
          clearTimeout(timeout);
          return resolve(response);
        }
      };
      this.addEventListener(waitResponse);
      this._send(payload).catch((err) => {
        reject(err);
      });
      timeout = window.setTimeout(() => {
        tries++;
        this.removeEventListener(waitResponse);
        clearTimeout(timeout);
        if (tries > MAX_TRIES) {
          return reject("Device no response");
        } else {
          console.warn("Device no response, try again", tries);
          this._sendAndWait(payload, tries++)
            .then(resolve)
            .catch(reject);
        }
      }, TIMEOUT_SEND_AND_WAIT);
    });
  }

  private async _send(payload: string): Promise<void> {
    if (!this.device) throw new Error("no device connected");
    const packets = payload.match(/.{1,20}/g) || [];
    for (const packet of packets) {
      console.log("sending packet", packet);
      await BleClient.writeWithoutResponse(
        this.device.deviceId,
        S4_SERVICE,
        S4_MAIN_CONSOLE_CHARACTERISTIC,
        textToDataView(packet)
      );
      await wait(100);
    }
    this.resetKeepAlive();
  }

  private resetKeepAlive() {
    this.removeKeepAlive();
    this.keepAliveInterval = window.setTimeout(() => {
      this.send(KEEP_ALIVE_MESSAGE);
    }, KEEP_ALIVE_TIME);
  }

  private removeKeepAlive() {
    if (this.keepAliveInterval) window.clearTimeout(this.keepAliveInterval);
  }

  private concatenateToBuffer(subCommand: string) {
    if (subCommand.startsWith(`{"len":"`)) this.buffer = subCommand;
    else this.buffer += subCommand;
  }

  private concatenateToBufferEvent(subCommand: string) {
    if (subCommand.startsWith(`{"`) && !this.bufferEvent.endsWith(":")) this.bufferEvent = subCommand;
    else this.bufferEvent += subCommand;
  }
}

const S4BluetoothConnector = new BluetoothService();

export default S4BluetoothConnector;
